import { useState, createRef, useRef } from "react";
import { FormikProps, FormikHelpers } from "formik";
import { useContext } from "react";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { RCA_PRIORITIES, RCA_STATUSES, USERTYPE } from "constants/constants";
import { IAddActionPlanFormValues } from "../ActionPlansTableInterfaces";

interface IUseActionPlanCreationProps {
    parentType: "rca" | "issue";
    parentId: number;
    onActionPlanCreated?: () => Promise<void> | void;
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
}

interface IUseActionPlanCreationResult {
    isCreatingActionPlan: boolean;
    isInlineFormVisible: boolean;
    setIsInlineFormVisible: (visible: boolean) => void;
    inlineFormRef: React.RefObject<FormikProps<IAddActionPlanFormValues>>;
    actionPlanNameInputRef: React.RefObject<HTMLInputElement>;
    handleCreate: (
        values: IAddActionPlanFormValues,
        formikHelpers: FormikHelpers<IAddActionPlanFormValues>,
    ) => Promise<void>;
    handleCancelCreation: () => void;
}

export const useActionPlanCreation = ({
    parentType,
    parentId,
    onActionPlanCreated,
    onSuccess,
    onError,
}: IUseActionPlanCreationProps): IUseActionPlanCreationResult => {
    const { createActionPlan } = ActionPlanEntity.InsertActionPlan();
    const { currentUser } = useContext(CorporateContext);
    const [isCreatingActionPlan, setIsCreatingActionPlan] = useState(false);
    const [isInlineFormVisible, setIsInlineFormVisible] = useState(false);
    const inlineFormRef = createRef<FormikProps<IAddActionPlanFormValues>>();
    const actionPlanNameInputRef = useRef<HTMLInputElement>(null);

    const handleCreate = async (
        values: IAddActionPlanFormValues,
        formikHelpers: FormikHelpers<IAddActionPlanFormValues>,
    ): Promise<void> => {
        setIsCreatingActionPlan(true);

        const baseParent = parentType === "rca" ? { rca_id: parentId } : { issue_id: parentId };

        const actionPlanValues = {
            ...baseParent,
            created_by: currentUser?.user_id,
            due_date: values.dueDate,
            submitted_by_user_id: currentUser?.user_id,
            assignee_id: values.assignedToId,
            priority_id: RCA_PRIORITIES.LOW,
            ...(currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.MANAGER
                ? {
                      status_id: RCA_STATUSES.NOT_STARTED,
                      approved_on: new Date(),
                      approver_id: currentUser?.user_id,
                  }
                : { status_id: RCA_STATUSES.NEEDS_APPROVAL }),
            name: values.actionPlanName,
        };

        try {
            await createActionPlan({ variables: { action_plan: actionPlanValues } });
            if (onActionPlanCreated) {
                await onActionPlanCreated();
            }
            formikHelpers.setFieldValue("actionPlanName", "");
            formikHelpers.setFieldTouched("actionPlanName", false);
            setTimeout(() => {
                actionPlanNameInputRef.current?.focus();
            }, 0);
            if (onSuccess) {
                onSuccess("rca.rca_library.rca_detail.action_plan_creation_success");
            }
        } catch (error) {
            if (onError) {
                onError("rca.rca_library.rca_detail.action_plan_creation_error");
            }
        } finally {
            setIsCreatingActionPlan(false);
        }
    };

    const handleCancelCreation = () => {
        inlineFormRef.current?.resetForm();
        setIsInlineFormVisible(false);
    };

    return {
        isCreatingActionPlan,
        isInlineFormVisible,
        setIsInlineFormVisible,
        inlineFormRef,
        actionPlanNameInputRef,
        handleCreate,
        handleCancelCreation,
    };
};

